import React from 'react';
import { FormControl, Select, MenuItem, SelectChangeEvent, useTheme, IconButton, Typography } from '@mui/material';
import useResponsive from '../../hooks/useResponsive';
import { useTranslation } from 'react-i18next';
import {
    Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useProjectContext } from '../../contexts/ProjectContext';
import { Project } from '../../interfaces/TaskOverview'

interface ProjectDropdownProps {
    projectList: Project[];
    handleProjectChange: (event: SelectChangeEvent<string>) => void;
    dropDownPress: () => void;
    getRefreshProjectList: () => void;
}

const ProjectDropdown: React.FC<ProjectDropdownProps> = ({
    projectList,
    handleProjectChange,
    dropDownPress,
    getRefreshProjectList,

}) => {
    const theme = useTheme();
    const { isMobile, isTablet, isDesktop } = useResponsive();
    const { t } = useTranslation();

    const {
        selectedProjectItem,
    } = useProjectContext();
    const iconColor = theme.palette.dropdownHighlight.main;
    const textColor = theme.palette.dropdownHighlight.contrastText;

    return (
        <FormControl
            sx={{
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                width: isMobile ? '100%' : null
            }}
        >
            <Select
                fullWidth={isMobile && true}
                displayEmpty
                value={projectList?.length > 0 ? JSON.stringify(selectedProjectItem) : ''}
                onChange={handleProjectChange}
                onClick={() => dropDownPress()}
                renderValue={(selected) => {
                    if (!selected) {
                        return <em style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t("taskOverViewScreen.pleaseSelectTheProject")}</em>;
                    }
                    const selectedProject = JSON.parse(selected as string);
                    return selectedProject.name;
                }}
                sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: '#2B2A5C',
                    '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '2px solid',
                        borderColor: '#BBBBBB'
                    },
                    width: isTablet || isDesktop ? 345 : '100%',
                    maxWidth: isTablet || isDesktop ? 345 : '100%',
                    height: isMobile ? 45 : 48,
                    ml: 2,
                    fontFamily: isMobile ? "Nunito Sans Regular400" : (isTablet ? 'Nunito Sans Regular500' : 'Nunito Sans Regular400'),
                    fontSize: isMobile ? '14px' : (isTablet ? '16px' : '18px'),
                    lineHeight: isMobile ? '24px' : (isTablet ? '24px' : '24px'),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    paddingRight: '28px',
                }}
            >
                {projectList.map((project: Project) => (
                    <MenuItem
                        key={project?.id || project?.project_name}
                        value={JSON.stringify({
                            id: project?.id,
                            name: project?.project_name,
                        })}
                        sx={{
                            '&.Mui-selected': {
                                backgroundColor: iconColor,
                                color: textColor,
                            },
                            '&.Mui-selected:hover': {
                                color: '#2B2A5C',
                            },
                            fontFamily: isMobile ? "Nunito Sans Regular400" : (isTablet ? 'Nunito Sans Regular500' : 'Nunito Sans Regular400'),
                            fontSize: isMobile ? '14px' : (isTablet ? '16px' : '18px'),
                            lineHeight: isMobile ? '24px' : (isTablet ? '24px' : '24px'),
                            color: '#2B2A5C',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            noWrap
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                wordBreak: 'break-all',
                            }}
                        >
                            {project?.project_name}
                        </Typography>
                    </MenuItem>
                ))}
            </Select>

            <IconButton
                sx={{
                    bgcolor: theme.palette.secondary.main,
                    "&:hover": { bgcolor: "action.hover" },
                    color: iconColor,
                    ml: 1,
                    p: 0
                }}
                onClick={getRefreshProjectList}
            >
                <RefreshIcon sx={{
                    height: 31.71,
                    width: 37,
                    p: 0
                }} />
            </IconButton>
        </FormControl>
    );
};

export default ProjectDropdown;